import { Suspense, useState, useCallback, useEffect, useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { useFBX, OrbitControls } from '@react-three/drei';
import styles from './Brain.module.css';
import { useTheme } from '@practium/features/theme';

interface MyModelProps {
	url: string;
	onLoad: (url: string) => void;
}

const MyModel = ({ url, onLoad }: MyModelProps) => {
	const fbxOriginal = useFBX(url);
	const fbx = useMemo(() => fbxOriginal.clone(true), [fbxOriginal]);

	useEffect(() => {
		onLoad(url);
	}, [fbx, onLoad, url]);

	return <primitive object={fbx} scale={0.03} position={[0, -0.35, 0]} />;
};

export default function Brain() {
	const { mode } = useTheme();
	const url = mode === 'dark' ? '/widgets/Brain_purple.fbx' : '/widgets/Brain_dark_blue.fbx';

	const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
	const isLoading = loadedUrl !== url;

	const handleLoad = useCallback((loaded: string) => {
		setLoadedUrl(loaded);
	}, []);

	return (
		<div className={styles.container}>

			<div
				className={styles.skeleton}
				style={{
					opacity: isLoading ? 1 : 0,
				}}
				aria-hidden={!isLoading}
			>
				<div className={styles.skeletonOrb}>
					<div className={styles.skeletonSphere} />
					<div className={styles.skeletonArc} />
					<div className={`${styles.skeletonArc} ${styles.skeletonArcOuter}`} />
				</div>
			</div>
			<Canvas
				camera={{ position: [0, 0, 5], fov: 50 }}
				dpr={[1, 1.5]}
				gl={{ antialias: false, alpha: true }}
			>
				<ambientLight intensity={0.5} />
				<directionalLight position={[10, 10, 10]} intensity={1} />

				<Suspense fallback={null}>
					<MyModel key={url} url={url} onLoad={handleLoad} />
				</Suspense>

				<OrbitControls
					enablePan={false}
					enableZoom={false}
					minPolarAngle={Math.PI / 2}
					maxPolarAngle={Math.PI / 2}
					autoRotate={true}
					autoRotateSpeed={3.0}
				/>
			</Canvas>
		</div>
	);
}
